import { createSelector } from 'reselect'
import { capitalise } from 'lib/string/stringUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export const getCustomOfferItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (order) => order ? order.customOfferItems : [],
)

export const getCustomOfferItemType = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (order) => {
    if (!order?.customOfferItems?.length || !order.customOfferItems[0].custom_offer.items.length) {
      return undefined
    }
    return order.customOfferItems[0].custom_offer.items[0].type
  },
)

export const getCustomOfferTravellers = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (order) => order?.customOfferItems?.length ? (order.customOfferItems[0].custom_offer.items[0].travellers ?? []) : [], // we support only one item at the moment
)

export function getCustomOfferTravellerBreakdown(travellers: Array<App.CustomOfferTraveller>) {
  const travellersInfo = travellers.reduce((acc, traveller) => {
    if (traveller.type === 'adult') {
      acc.adults += 1
    } else if (traveller.type === 'child') {
      acc.children += 1
    } else if (traveller.type === 'teenager') {
      acc.teenagers += 1
    } else {
      acc.infants += 1
    }

    return acc
  }, { adults: 0, children: 0, infants: 0, teenagers: 0 })

  return travellersInfo
}

export const getCustomOfferBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (order): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (!order?.customOfferItems?.length) {
      return {
        hasRequiredData: true,
        data: [],
      }
    }

    const orderItem = order.customOfferItems[0]
    const customOffer = orderItem.custom_offer
    const customItem = customOffer.items[0]

    const travellers = getCustomOfferTravellerBreakdown(customItem.travellers)

    const intl = getCurrentIntl()
    const parts = [
      intl.formatMessage({
        id: 'tio1eIRlLn',
        defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
        description: 'Adult count in custom offer traveller breakdown',
      }, { count: travellers.adults }),
    ]
    if (travellers.teenagers > 0) {
      parts.push(intl.formatMessage({
        id: 'iGT+4HAvQC',
        defaultMessage: '{count, plural, one {# Teenager} other {# Teenagers}}',
        description: 'Teenager count in custom offer traveller breakdown',
      }, { count: travellers.teenagers }))
    }
    if (travellers.children > 0) {
      parts.push(intl.formatMessage({
        id: 'x9BstpLCkD',
        defaultMessage: '{count, plural, one {# Child} other {# Children}}',
        description: 'Child count in custom offer traveller breakdown',
      }, { count: travellers.children }))
    }
    if (travellers.infants > 0) {
      parts.push(intl.formatMessage({
        id: 'OJps4NVYXz',
        defaultMessage: '{count, plural, one {# Infant} other {# Infants}}',
        description: 'Infant count in custom offer traveller breakdown',
      }, { count: travellers.infants }))
    }
    const travellersBreakdown = parts.join(', ')
    const price = customOffer.price / 100
    const memberPrice = customOffer.memberPrice / 100
    const depositAmount = (customOffer?.payment_metadata?.customer_deposit_1_amount ?? 0) / 100

    let customOfferItemBreakdownView: App.Checkout.CustomOfferItemBreakdownView | App.Checkout.CruiseItemBreakdownView

    /*
      Cruise is the only price breakdown component which a corresponding custom offer maps
      to correctly. So we can use generic custom offer price breakdown component for other
      types for now until we collect more data in the custom offers form.
    */
    if (customItem.type === 'cruise') {
      customOfferItemBreakdownView = {
        title: intl.formatMessage(
          {
            id: 'yTHaopQ61o',
            defaultMessage: '{shipName} · {shipCabin}',
            description: 'Cruise ship name and cabin shown in custom offer price breakdown',
          },
          {
            shipName: customItem.ship_name,
            shipCabin: customItem.ship_cabin,
          },
        ),
        additionalInfo: [{ text: travellersBreakdown }],
        additionalElements: [],
        price: price - depositAmount,
        memberPrice: memberPrice - depositAmount,
        itemType: 'cruise',
      }
    } else {
      customOfferItemBreakdownView = {
        title: '',
        itemType: 'customOffer',
        additionalInfo: [{ text: travellersBreakdown }],
        additionalElements: [],
        price: price - depositAmount,
      }
    }

    const item: App.Checkout.PriceBreakdownView = {
      title: capitalise(customItem.type),
      price: price - depositAmount,
      memberPrice: memberPrice - depositAmount,
      description: customOffer.name,
      additionalInfoText: [],
      items: [
        customOfferItemBreakdownView,
      ],
    }

    return {
      hasRequiredData: true,
      data: [item],
    }
  },
)
